import type {
  OnsiteUpsellConfig,
  OnsiteUpsellProduct,
  OnsiteUpsellProductResolved,
  OnsiteUpsellSlot,
  UpsellDiscount,
} from "@intelligems/ig-types";

import {
  shopifyPercentageDiscountPerUnit,
  shouldFlushCompareAtPrice,
} from "./price";

// Onsite-upsell template grammar — shared by the plugin (fills templates) and
// viv (authors them). Slot names live in ig-types (OnsiteUpsellSlot).
export const UPSELL_EACH_OPEN = "{{#each products}}";
export const UPSELL_EACH_CLOSE = "{{/each}}";

export const upsellSlotToken = (slot: OnsiteUpsellSlot): string =>
  `{{${slot}}}`;

// Canonical designed templates (the Figma "Upsell templates" look), single-
// sourced for the FE default, viv's knowledge, and the panel picker. Storefront
// HTML, so raw hex + a scoped <style> block.
const UPSELL_STYLES = `
    .ig-upsell{box-sizing:border-box;margin-top:16px;padding:16px;border:1px solid #d4d4d4;border-radius:6px;background:#ffffff;font-family:inherit;color:inherit;text-align:left;}
    .ig-upsell *,.ig-upsell *::before,.ig-upsell *::after{box-sizing:border-box;}
    .ig-upsell__title{margin:0 0 12px;font-size:15px;line-height:22px;font-weight:600;}
    .ig-upsell__items{display:flex;flex-direction:column;gap:8px;}
    .ig-upsell__row{display:flex;align-items:center;gap:12px;}
    .ig-upsell__media{flex:0 0 auto;width:64px;height:64px;border-radius:8px;overflow:hidden;display:block;}
    .ig-upsell__media img{width:100%;height:100%;object-fit:cover;display:block;}
    .ig-upsell__info{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:2px;}
    .ig-upsell__name{margin:0;font-size:14px;font-weight:600;line-height:1.5;color:inherit;text-decoration:none;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
    .ig-upsell__price{display:flex;gap:6px;align-items:baseline;font-size:12px;line-height:1.5;color:inherit;}
    .ig-upsell [ig-onsite-upsell-compare-price]{color:#8a8a8a;text-decoration:line-through;}
    .ig-upsell [ig-onsite-upsell-compare-price]:empty{display:none;}
    .ig-upsell [ig-onsite-upsell-variant-select]{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;}
    .ig-upsell [ig-onsite-upsell-variant-select]:empty{display:none;}
    .ig-upsell [ig-onsite-upsell-variant-select] select{font-family:inherit;font-size:12px;line-height:1.5;padding:4px 6px;border:1px solid #d4d4d4;border-radius:4px;background:#ffffff;color:inherit;max-width:100%;min-width:0;text-overflow:ellipsis;}
    .ig-upsell__action{flex:0 0 auto;}
    .ig-upsell [ig-onsite-upsell-atc]{appearance:none;border:0;border-radius:2px;background:#303030;color:#ffffff;font-family:inherit;font-size:14px;font-weight:600;line-height:1.5;padding:8px 12px;cursor:pointer;white-space:nowrap;}
    .ig-upsell [ig-onsite-upsell-atc]:hover{opacity:0.92;}
    .ig-upsell [ig-onsite-upsell-atc]:disabled{opacity:0.5;cursor:not-allowed;}`;

const listTemplate = `<div class="ig-upsell">
  <style>${UPSELL_STYLES}
  </style>
  <div class="ig-upsell__title">You may also like</div>
  <div class="ig-upsell__items">
  ${UPSELL_EACH_OPEN}
    <div class="ig-upsell__row">
      <a class="ig-upsell__media" href="${upsellSlotToken("product.url")}" ig-onsite-upsell-link>
        <img src="${upsellSlotToken("product.image")}" alt="${upsellSlotToken("product.title")}" />
      </a>
      <div class="ig-upsell__info">
        <a class="ig-upsell__name" href="${upsellSlotToken("product.url")}" ig-onsite-upsell-link>${upsellSlotToken("product.title")}</a>
        <div class="ig-upsell__price">${upsellSlotToken("product.price")}${upsellSlotToken("product.compareAtPrice")}</div>
        ${upsellSlotToken("variant-select")}
      </div>
      <div class="ig-upsell__action">${upsellSlotToken("atc")}</div>
    </div>
  ${UPSELL_EACH_CLOSE}
  </div>
</div>`;

export const ONSITE_UPSELL_TEMPLATES = {
  list: listTemplate,
} as const;

export type OnsiteUpsellTemplateLayout = keyof typeof ONSITE_UPSELL_TEMPLATES;

// Template → HTML runtime, shared by the plugin and the FE preview so the
// rendered markup can't drift. The plugin queries these attrs to hydrate cards;
// the FE preview renders the same markup inert.
export const ATC_MARKER = "ig-onsite-upsell-atc";
export const VARIANT_SELECT_ATTR = "ig-onsite-upsell-variant-select";
export const CARD_ATTR = "ig-onsite-upsell-card";
export const PRICE_ATTR = "ig-onsite-upsell-price";
export const COMPARE_PRICE_ATTR = "ig-onsite-upsell-compare-price";

export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

// Substitutes the {{slot}} tokens in one product's template block.
export function fillItem(
  itemTemplate: string,
  product: OnsiteUpsellProduct
): string {
  return itemTemplate
    .replaceAll(upsellSlotToken("product.image"), escapeHtml(product.image))
    .replaceAll(upsellSlotToken("product.url"), escapeHtml(product.url))
    .replaceAll(upsellSlotToken("product.title"), escapeHtml(product.title))
    .replaceAll(
      upsellSlotToken("product.price"),
      `<span ${PRICE_ATTR}>${escapeHtml(product.price)}</span>`
    )
    .replaceAll(
      upsellSlotToken("product.compareAtPrice"),
      `<span ${COMPARE_PRICE_ATTR}>${escapeHtml(product.compareAtPrice)}</span>`
    )
    .replaceAll(
      upsellSlotToken("variant-select"),
      `<div ${VARIANT_SELECT_ATTR}></div>`
    )
    .replaceAll(
      upsellSlotToken("atc"),
      `<button ${ATC_MARKER} type="button">Add to cart</button>`
    );
}

// Splits a template into the fixed shell and the {{#each products}}…{{/each}}
// block that repeats per product. No markers → the whole template is one item.
export function parseTemplate(template: string): {
  before: string;
  itemTemplate: string;
  after: string;
} {
  const blockStart = template.indexOf(UPSELL_EACH_OPEN);
  const blockEnd = template.indexOf(UPSELL_EACH_CLOSE);

  const hasItemBlock =
    blockStart !== -1 && blockEnd !== -1 && blockStart < blockEnd;
  if (!hasItemBlock) {
    return { before: "", itemTemplate: template, after: "" };
  }

  return {
    before: template.slice(0, blockStart),
    itemTemplate: template.slice(
      blockStart + UPSELL_EACH_OPEN.length,
      blockEnd
    ),
    after: template.slice(blockEnd + UPSELL_EACH_CLOSE.length),
  };
}

// Fills one product's template, wrapped in a display:contents container tagged
// with its index (links the rendered card back to its product for hydration).
export function productCardHtml(
  itemTemplate: string,
  product: OnsiteUpsellProduct,
  cardIndex: number
): string {
  return `<div ${CARD_ATTR}="${cardIndex}" style="display:contents">${fillItem(
    itemTemplate,
    product
  )}</div>`;
}

// The fixed template shell around one card per product.
export function renderUpsellTemplate(
  template: string,
  products: OnsiteUpsellProduct[]
): string {
  const { before, itemTemplate, after } = parseTemplate(template);
  return (
    before +
    products
      .map((product, index) => productCardHtml(itemTemplate, product, index))
      .join("") +
    after
  );
}

// The endpoint returns raw prices; the card shows the discounted price. Shared
// by the plugin and the FE preview (only the money formatter is injected).
// Percentage discounts floor to the cent like Shopify.
export function applyUpsellDiscount(
  price: number,
  discount: UpsellDiscount | undefined
): number {
  if (
    !discount ||
    discount.discountType === "none" ||
    discount.discountValue == null
  ) {
    return price;
  }
  if (discount.discountType === "percentage") {
    return (
      price - shopifyPercentageDiscountPerUnit(price, discount.discountValue)
    );
  }
  return Math.max(0, price - discount.discountValue);
}

// The struck-through reference price; `compareAtPriceDisplay` picks which one.
function upsellCompareAtPrice(
  regularPrice: number,
  shopifyCompareAt: number | undefined,
  discount: UpsellDiscount | undefined
): number | undefined {
  if (!discount || discount.discountType === "none") {
    return shopifyCompareAt;
  }
  switch (discount.compareAtPriceDisplay) {
    case "regularPrice":
      return regularPrice;
    case "compareAtPrice":
      return shopifyCompareAt;
    default:
      return undefined;
  }
}

// Maps a resolved product (numeric prices) to a render product (formatted,
// discount-applied strings).
export function toOnsiteUpsellRenderProduct(
  product: OnsiteUpsellProductResolved,
  discount: UpsellDiscount | undefined,
  formatMoney: (amount: number, currencyCode: string) => string
): OnsiteUpsellProduct {
  const renderPrice = (price: number, compareAt: number | undefined) => {
    const discounted = applyUpsellDiscount(price, discount);
    const compare = upsellCompareAtPrice(price, compareAt, discount);
    return {
      price: formatMoney(discounted, product.currencyCode),
      compareAtPrice:
        compare != null && !shouldFlushCompareAtPrice(discounted, compare)
          ? formatMoney(compare, product.currencyCode)
          : "",
    };
  };

  return {
    productId: product.productId,
    url: product.url,
    title: product.title,
    image: product.image,
    ...renderPrice(product.price, product.compareAtPrice ?? undefined),
    options: product.options,
    ...(product.defaultVariantId === undefined
      ? {}
      : { defaultVariantId: product.defaultVariantId }),
    variants: product.variants.map((variant) => ({
      id: variant.id,
      availableForSale: variant.availableForSale,
      image: variant.image,
      ...renderPrice(variant.price, variant.compareAtPrice ?? undefined),
      options: variant.options,
      sig: variant.sig,
    })),
  };
}

/**
 * True when an onsite upsell is served by the Agentic Logic engine rather than
 * by its legacy `rules`.
 *
 * A saved binding (`agenticLogicPlacementId`) or a rule the author picked in
 * the builder but has not saved yet (`agenticLogicRuleId`) both mean the
 * engine owns it. Either way `rules` is only the legacy rollback config and
 * must not be read — it is never empty, because the schema requires it.
 */
export function isAgenticLogicOnsiteUpsell(
  config: Pick<
    OnsiteUpsellConfig,
    "agenticLogicPlacementId" | "agenticLogicRuleId"
  >
): boolean {
  return !!config.agenticLogicPlacementId || !!config.agenticLogicRuleId;
}

/**
 * The products an onsite upsell renders, from what its resolve returned.
 *
 * A component served by the Agentic Logic engine renders what the server
 * returned: the rule's `settings.maxItems` owns the count and the server
 * already trimmed to it, so the container's own `maxProductLimit` — kept only
 * for the legacy engine — must not cap it a second time. A legacy component
 * resolves `maxProductLimit` plus a rotation reserve, so it slices here.
 */
export function onsiteUpsellProductsToRender<T>(
  config: Pick<
    OnsiteUpsellConfig,
    "maxProductLimit" | "agenticLogicPlacementId" | "agenticLogicRuleId"
  >,
  products: readonly T[]
): T[] {
  return isAgenticLogicOnsiteUpsell(config)
    ? [...products]
    : products.slice(0, config.maxProductLimit);
}
